import React, { useEffect, useRef, useState } from 'react';

import { ComponentWrapper } from '../../../helperComponents/ComponentWrapper';
import { DropdownMenu } from '../../../helperComponents/DropdownMenu/DropdownMenu';
import { Image, Platform, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { applyTransparency } from '../../../../constants/colors';
import { MenuButton } from '../../../helperComponents/MenuButton';

import { GAME_TYPES_TYPE, TAnswerPair } from '../../../../constants/constants';
import AssessmentTemplate from '../../../helperComponents/Assessment/AssessmentTemplate';
import { BackButtonV2 } from '../../../helperComponents/BackButtonV2';
import LabelCountBox from '../../../helperComponents/LabelCountBox';
import AssessmentResultPopUp from '../../../helperComponents/Journey/AssessmentResultPopUp';
import { useSignedInContext } from '../../../../context/SignedInContext';
import { EGymAssessmentMode } from '../../../../utils/gymUtils';
import { isMobile } from '@mightybyte/rnw.utils.device-info';
import { useGetHivesQuery } from '../../MyHives/useGetHivesQuery.gql';
import { useIncrementGymAssessmentPracticeGamesPlayedMutation } from '../Stats/useIncrementGymAssessmentPracticeGamesPlayedMutation.gql';
const bee = require('../../../../resources/images/backgroundBeeRightv2.png');

export default function GymAssessment({ navigation, route }) {
  const [assessmentMode, setAssessmentMode] = useState<EGymAssessmentMode | undefined>();
  const gameType = route.params?.gameType as GAME_TYPES_TYPE;

  const { user } = useSignedInContext();
  const userId = user?.id;

  const { error: getHivesError, data: getHivesData, refetch: refetchHive } = useGetHivesQuery();

  const { width } = useWindowDimensions();
  const pathSizeLimit = width < 1161;
  const isMobileWeb = Platform.OS === 'web' && width <= 768;

  const [score, setScore] = useState(0);
  const [questionIndex, setQuestionIndex] = useState(0);

  const [complete, setComplete] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  // 🕒 Timer state
  const [time, setTime] = useState(0); // in seconds
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [averageTimePerAnswer, setAverageTimePerAnswer] = useState(0);

  const fixedQuestionsCount = 25;

  const secondsAllowedTimed = 60;

  // 🕒 Timer state
  const [timeLeft, setTimeLeft] = useState(secondsAllowedTimed);

  const [incrementPracticeGame] = useIncrementGymAssessmentPracticeGamesPlayedMutation();

  const [correctAnswers, setCorrectAnswers] = useState<TAnswerPair[]>([]);
  const [wrongAnswers, setWrongAnswers] = useState<TAnswerPair[]>([]);

  // Start timer only when Buzz Game starts
  useEffect(() => {
    if (assessmentMode === EGymAssessmentMode.FixedQuestions && !complete) {
      // reset timer
      setTime(0);
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }

    // cleanup when leaving game or unmounting
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [assessmentMode, complete]);

  // reset helper
  const resetGame = () => {
    setScore(0);
    setQuestionIndex(0);
    setFinalScore(0);
    setComplete(false);
    setAssessmentMode(undefined);
    setTimeLeft(60); // reset timer
  };

  const handleSubmit = async () => {
    try {
      navigation.navigate('SelectGameType', {
        gym: true,
        gymAssessment: true,
      });
    } catch (err) {
      console.error(err);
      console.log('Error', 'Failed to submit Assessment.');
    }
  };

  const handleComplete = async ({
    finalScore,
    correctAnswers = [],
    wrongAnswers = [],
  }: {
    finalScore: number;
    correctAnswers?: TAnswerPair[];
    wrongAnswers?: TAnswerPair[];
  }) => {
    setFinalScore(finalScore);
    setShowCompleteModal(true);
    setComplete(true);

    // 🛑 stop timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    try {
      if (!userId || !assessmentMode) return;

      await incrementPracticeGame({
        variables: {
          input: {
            userId,
            gameType,
            assessmentMode,
            highScore: score,
            correctCount: score,
            questionsTaken:
              assessmentMode === EGymAssessmentMode.FixedQuestions
                ? fixedQuestionsCount
                : questionIndex,
            timeSec: assessmentMode === EGymAssessmentMode.Timed ? secondsAllowedTimed : time,
            correctAnswers,
            wrongAnswers,
          },
        },
      });
    } catch (err) {
      console.error('Failed to update practice stats', err);
    }
  };

  // 🕒 Timer effect
  useEffect(() => {
    if (assessmentMode === EGymAssessmentMode.Timed && !complete) {
      if (timeLeft <= 0) {
        handleComplete({ finalScore: score, correctAnswers, wrongAnswers }); // end game when time is up
        return;
      }
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [assessmentMode, timeLeft, complete, score]);

  return (
    <ComponentWrapper
      backgroundColor="#FCEABB8C"
      hasBackground={false}
      hasInset
      scrollEnabled={false}
      style={{ paddingHorizontal: 0 }}
      hideBee={true}
    >
      <DropdownMenu
        backButton={
          <BackButtonV2
            onPress={() => {
              resetGame();
              if (navigation.canGoBack()) {
                navigation.goBack();
              } else {
                navigation.navigate('SelectGameType', {
                  gym: true,
                  gymAssessment: true,
                });
              }
            }}
          />
        }
        top={20}
      />

      {!assessmentMode && (
        <>
          <Image
            resizeMode="cover"
            style={[styles.bee, isMobile && { bottom: -90, right: 50 }]}
            source={bee}
          />
          <Text style={[styles.title, isMobile && { marginTop: 150 }]}>Buzz Practice</Text>
          <Text style={[styles.subTitle]}>Choose type of practice</Text>
          <MenuButton
            onPress={() => {
              setAssessmentMode(EGymAssessmentMode.Timed);
            }}
            colors={['#FEDD88', '#FFCF53']}
            text="Timed"
            width={302}
            height={65}
            style={{ marginTop: 46 }}
            textStyle={{
              fontSize: 21,
              fontFamily: 'Secular One',
              color: '#000000',
              fontWeight: '400',
            }}
          />
          <MenuButton
            onPress={() => {
              setAssessmentMode(EGymAssessmentMode.FixedQuestions);
            }}
            colors={['#90DF75', '#62B655']}
            text="25 Questions"
            width={302}
            height={65}
            style={{ marginTop: 18 }}
            textStyle={{
              fontSize: 21,
              fontFamily: 'Secular One',
              color: '#000000',
              fontWeight: '400',
            }}
          />
        </>
      )}

      {assessmentMode && (
        <View
          style={[
            pathSizeLimit ? styles.gameStatusContainerSmall : styles.gameStatusContainer,
            isMobile && { marginTop: 80 },
            isMobileWeb && { marginTop: 130 },
          ]}
        >
          <View style={{ marginRight: pathSizeLimit ? 10 : 0 }}>
            <LabelCountBox
              label={'Score'}
              countText={`${score}/${
                assessmentMode === EGymAssessmentMode.FixedQuestions
                  ? fixedQuestionsCount
                  : questionIndex
              }`}
            />
          </View>

          <LabelCountBox
            label={
              assessmentMode === EGymAssessmentMode.FixedQuestions ? 'Questions Left' : 'Time Left'
            }
            countText={
              assessmentMode === EGymAssessmentMode.FixedQuestions
                ? `${fixedQuestionsCount - questionIndex}`
                : `${timeLeft}s`
            }
            width={170}
          />
        </View>
      )}

      {assessmentMode && !complete && (
        <AssessmentTemplate
          gameType={gameType}
          questionsCount={fixedQuestionsCount}
          setQuestionIndex={setQuestionIndex}
          setScore={setScore}
          onComplete={handleComplete}
          mode={assessmentMode}
          setCorrectAnswers={setCorrectAnswers}
          setWrongAnswers={setWrongAnswers}
        />
      )}

      {showCompleteModal && (
        <AssessmentResultPopUp
          positive={true}
          title={'Done'}
          subtitle={'Great job!'}
          description={`You have completed the assessment gym practice!`}
          onClose={() => setShowCompleteModal(false)}
          visible={showCompleteModal}
          setVisible={setShowCompleteModal}
          practice={true}
          buttonText1={'Practice Again'}
          buttonText2={'Go Back to Gym'}
          buttonFunction1={() => {
            setShowCompleteModal(false);
            resetGame();
          }}
          buttonFunction2={handleSubmit}
        />
      )}
    </ComponentWrapper>
  );
}

const styles = StyleSheet.create({
  mainContainer: {
    marginTop: 100,
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 28,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#E87030',
    marginTop: 100,
  },
  subTitle: {
    fontFamily: 'Secular One',
    fontSize: 18,
    fontWeight: '400',
    color: '#000000',
    marginTop: 18,
  },
  gameStatusContainer: {
    position: 'absolute',
    top: 20,
    right: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 20,
  },
  gameStatusContainerSmall: {
    marginTop: 30,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 20,
  },
  bee: {
    width: 280.47,
    height: 283.61,
    position: 'absolute',
    bottom: '10%',
    right: '30%',
  },
});
